<template>
  <div id="search">
    <!-- Left panel -->
    <div class="left-panel">
      <div class="search-bar-section">
        <v-text-field
          id="searchresultbar"
          v-model="search.query"
          placeholder="Rechercher un Client / Prospect / Projet"
          outlined
          class="rounded-lg"
          color="#FFF"
          background-color="#fff"
          hide-details
          @keyup.enter="searchEntreprise"
          dense
        >
          <template slot="append">
            <img
              @click="searchEntreprise"
              src="@/assets/Rech Avancée.png"
              alt="Recherche avancée"
            />
          </template>
        </v-text-field>
      </div>

      <div class="filters-section">
        <h2>AFFICHER SEULEMENT</h2>
        <div class="filter-chips">
          <button
            v-for="f in filters"
            :key="f.key"
            type="button"
            class="filter-chip"
            :class="{ 'filter-chip--active': search[f.key] }"
            @click="toggleFilter(f.key)"
          >
            {{ f.label }}
          </button>
        </div>
        <hr class="hr mt-3" />
        <h2 class="mt-3">RÉSULTATS ({{ search.nb_result }})</h2>
      </div>

      <div class="results-section">
        <div v-if="entreprises.length === 0" class="no-results">
          <v-icon color="#ccc" size="36">mdi-magnify-close</v-icon>
          <p>Aucun résultat</p>
        </div>

        <div v-for="(entreprise, index) in entreprises" :key="entreprise.id_entreprise">
          <SearchResult
            :i="index"
            :max="search.nb_result"
            :entreprise="entreprise"
          />
        </div>

        <div v-if="search.nbPage > 1" class="text-center pt-2 pb-3">
          <v-pagination
            v-model="page"
            :length="search.nbPage"
            circle
          ></v-pagination>
        </div>
      </div>
    </div>

    <!-- Right panel -->
    <div class="right-panel">
      <div class="map-section">
        <Carte
          :query="search.query"
          :filter="{
            clients: search.clients,
            visite: search.visite,
            projets: search.projets,
            prospects: search.prospects,
            no_visite: search.no_visite,
            no_projet: search.no_projet,
          }"
          :mode="'result'"
          :initialCenter="initialView.center"
          :initialZoom="initialView.zoom"
          @view-changed="onViewChanged"
        />
      </div>
      <div class="legend-section">
        <h3>Légende :</h3>
        <ul id="legende">
          <li class="mr-5" v-for="(l, i) in legende" :key="i">
            <i class="fa fa-map-marker-alt" :class="l.class"></i>
            {{ l.text }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Carte from "@/components/Home/Carte";
import apiEntreprises from "@/api/entreprise";
import SearchResult from "../components/Home/SearchResult.vue";
import { readState, buildQuery } from "@/helpers/searchState";

export default {
  name: "SearchEntreprise",
  components: { Carte, SearchResult },
  data() {
    const state = readState(this.$route.query);
    return {
      search: {
        query: this.$route.query.recherche,
        ...state.filters,
        nb_result: 0,
        nbPage: 1,
      },
      page: state.page,
      initialView: state.view,
      view: null,
      debounceTimer: null,
      viewTimer: null,
      entreprises: [],
      filters: [
        { key: 'clients',    label: 'Clients' },
        { key: 'prospects',  label: 'Prospects' },
        { key: 'visite',     label: 'Visité' },
        { key: 'no_visite',  label: 'Non visité' },
        { key: 'projets',    label: 'Projets' },
        { key: 'no_projet',  label: 'Sans projet' },
      ],
      legende: [
        { text: "Client avec projet",                class: "client_projet" },
        { text: "Client sans projet",                class: "client_no_projet" },
        { text: "Prospect non visité sans projet",   class: "prospect_no_visite_no_projet" },
        { text: "Prospect non visité avec projet",   class: "prospect_no_visite_projet" },
        { text: "Prospect visité sans projet",       class: "prospect_visite_no_projet" },
        { text: "Prospect visité avec projet",       class: "prospect_visite_projet" },
      ],
    };
  },
  watch: {
    page() {
      this.getEntreprises({ size: process.env.VUE_APP_NB_DISPLAY, page: this.page - 1 });
      this.saveState();
    },
    'search.query'() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.searchEntreprise();
      }, 500);
    },
  },
  methods: {
    toggleFilter(key) {
      this.search[key] = !this.search[key];
      this.searchEntreprise();
    },

    buildFilter() {
      return {
        query:     this.search.query,
        clients:   this.search.clients,
        prospects: this.search.prospects,
        visite:    this.search.visite,
        noVisite:  this.search.no_visite,
        projets:   this.search.projets,
        noProjet:  this.search.no_projet,
      };
    },

    getEntreprises({ size, page = 0 } = {}) {
      const f = this.buildFilter();
      apiEntreprises
        .getEntreprises(size, f.query, f.clients, f.prospects, f.visite, f.noVisite, f.projets, f.noProjet, page)
        .then((res) => {
          this.entreprises = res.data.entreprise.rows;
          this.search.nbPage = res.data.entreprise.totalPages;
          this.search.nb_result = res.data.entreprise.totalItems;
        }).catch(() => {
          this.$store.dispatch('notify', { message: 'Erreur lors du chargement' });
        });
    },

    // A new search or filter always restarts on page 1; the page watcher loads it
    searchEntreprise() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.getEntreprises({ size: process.env.VUE_APP_NB_DISPLAY });
        this.saveState();
      }
    },

    onViewChanged(view) {
      this.view = view;
      clearTimeout(this.viewTimer);
      this.viewTimer = setTimeout(() => this.saveState(), 300);
    },

    saveState() {
      const view = this.view || (this.initialView.center ? { lat: this.initialView.center[0], lng: this.initialView.center[1], zoom: this.initialView.zoom } : null);
      const query = buildQuery(this.search, this.page, view);
      // Rewrite the current history entry in place: the back button still leaves the search page,
      // and router guards (auth check API call) do not run on every map move
      const href = this.$router.resolve({ name: this.$route.name, query }).href;
      window.history.replaceState(window.history.state, '', href);
    },
  },
  created() {
    this.getEntreprises({ size: process.env.VUE_APP_NB_DISPLAY, page: this.page - 1 });
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.viewTimer);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

// ── Page layout ─────────────────────────────────────────────────────
#search {
  display: flex;
  height: 100%;
  overflow: hidden;
}

// ── Left panel ──────────────────────────────────────────────────────
.left-panel {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-right: 1px solid #e0e0e0;
}

.search-bar-section {
  background-color: $background-color-blue;
  padding: 12px 16px;
  flex-shrink: 0;
}

.filters-section {
  padding: 16px 16px 8px;
  flex-shrink: 0;

  h2 {
    font-size: 1.1em;
    font-weight: bold;
    color: $background-color-blue;
    text-align: left;
    margin-bottom: 8px;
  }

  .hr {
    color: $table-background-secondary-color;
  }
}

.results-section {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
}

// ── Right panel ─────────────────────────────────────────────────────
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.map-section {
  flex: 1;
  overflow: hidden;

  // Carte component fills this container
  ::v-deep > * {
    height: 100%;
  }
}

.legend-section {
  flex-shrink: 0;
  padding: 8px 16px 12px;

  h3 {
    text-align: left;
    margin-bottom: 4px;
  }
}

// ── Search input ────────────────────────────────────────────────────
::v-deep #searchresultbar::placeholder {
  font-size: 14px;
}

::v-deep .v-input input,
.v-input input:focus,
input:focus,
input {
  caret-color: black;
}

::v-deep
  .v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined
  .v-input__append-inner {
  margin: 1% auto 0;
  cursor: pointer;
}

// ── Filter chips ───────────────────────────────────────────────────
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  border: 1.5px solid #b3d9ec;
  background: #fff;
  color: $background-color-blue;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  outline: none;

  &:hover {
    background: #e6f4fa;
  }

  &--active {
    background: $background-color-blue;
    color: #fff;
    border-color: $background-color-blue;

    &:hover {
      background: darken(#0B87BA, 6%);
    }
  }
}

// ── Empty state ────────────────────────────────────────────────────
.no-results {
  text-align: center;
  padding: 32px 0;
  color: #bbb;

  p {
    margin-top: 8px;
    font-size: 0.9rem;
  }
}

// ── Legend ─────────────────────────────────────────────────────────
#legende {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;

  li {
    display: inline-flex;
    align-items: center;
  }

  .fa-map-marker-alt {
    margin-right: 6px;

    &.client_projet                { color: chartreuse; }
    &.client_no_projet             { color: blue; }
    &.prospect_visite_projet       { color: orange; }
    &.prospect_visite_no_projet    { color: black; }
    &.prospect_no_visite_projet    { color: rgb(235, 57, 235); }
    &.prospect_no_visite_no_projet { color: red; }
  }
}
</style>
